// src/hooks/useQRBuilder.ts
import React from 'react';
import { DEFAULT_SETTINGS } from '../types/qr';
import { getQRMatrix } from '../lib/qr-matrix';
import { renderQR } from '../lib/qr-renderer';
export function useQRBuilder() {
    const previewRef = React.useRef(null);
    const regenTimer = React.useRef(null);
    const [url, setUrl] = React.useState('https://adora.media');
    const [ready, setReady] = React.useState(false);
    const [exportSize, setExportSize] = React.useState(800);
    const [previewSize, setPreviewSize] = React.useState(270);
    const [previewScale, setPreviewScale] = React.useState(2);
    const [settings, setSettings] = React.useState(DEFAULT_SETTINGS);
    const [matrix, setMatrix] = React.useState(null);
    const update = React.useCallback((key, value) => {
        setSettings((prev) => ({ ...prev, [key]: value }));
    }, []);
    // Debounce matrix regeneration on URL changes
    React.useEffect(() => {
        if (regenTimer.current !== null)
            window.clearTimeout(regenTimer.current);
        regenTimer.current = window.setTimeout(() => {
            const next = getQRMatrix(url);
            setMatrix(next);
            setReady(Boolean(next));
        }, 250);
        return () => {
            if (regenTimer.current !== null)
                window.clearTimeout(regenTimer.current);
        };
    }, [url]);
    // Render whenever matrix/settings/preview sizing changes
    React.useEffect(() => {
        if (!matrix)
            return;
        const canvas = previewRef.current;
        if (!canvas)
            return;
        const pixelSize = Math.max(64, Math.floor(previewSize * previewScale));
        // css display size
        canvas.style.width = `${previewSize}px`;
        canvas.style.height = `${previewSize}px`;
        requestAnimationFrame(() => {
            const c = previewRef.current;
            if (!c)
                return;
            renderQR(matrix, c, pixelSize, settings);
        });
    }, [matrix, settings, previewSize, previewScale]);
    const downloadPng = React.useCallback(() => {
        if (!matrix)
            return;
        const tmp = document.createElement('canvas');
        renderQR(matrix, tmp, exportSize, settings);
        const a = document.createElement('a');
        a.download = 'adora-qr.png';
        a.href = tmp.toDataURL('image/png');
        a.click();
    }, [matrix, exportSize, settings]);
    const downloadSvg = React.useCallback(() => {
        if (!matrix)
            return;
        const tmp = document.createElement('canvas');
        renderQR(matrix, tmp, exportSize, settings);
        const dataUrl = tmp.toDataURL('image/png');
        const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${exportSize}" height="${exportSize}">
  <image href="${dataUrl}" width="${exportSize}" height="${exportSize}" />
</svg>`;
        const blob = new Blob([svgContent], { type: 'image/svg+xml' });
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.download = 'adora-qr.svg';
        a.href = blobUrl;
        a.click();
        URL.revokeObjectURL(blobUrl);
    }, [matrix, exportSize, settings]);
    return React.useMemo(() => ({
        url,
        setUrl,
        settings,
        update,
        ready,
        previewRef,
        previewSize,
        setPreviewSize,
        previewScale,
        setPreviewScale,
        exportSize,
        setExportSize,
        downloadPng,
        downloadSvg,
    }), [url, settings, update, ready, previewSize, previewScale, exportSize, downloadPng, downloadSvg]);
}
